import {
  Flex,
  Text,
  useColorModeValue,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Switch,
  Stack,
  Box,
  Select,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import SearchTable2 from 'components/Tables/SearchTable2';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { totalRow } from 'state/roles-permissions';
import { AddIcon } from '@chakra-ui/icons';
import { checkForbidden } from 'helpers/checkForbidden';
import { BsSearch } from 'react-icons/bs';
import { RxReset } from 'react-icons/rx';
import { useChannelGroupActions } from 'hooks/useChannelGroupActions';
import { listChannelGroupAtom } from 'state/show';
import { columnsDataChannelGroup } from 'variables/columnsData';
import { STATUS } from 'variables/general';

const LEVEL1_OPTIONS = ['B2B', 'B2C', 'Private Show'];
const LEVEL2_OPTIONS = ['B2B&B2C', 'Private Show'];

function DataTableChannelGroups() {
  const textColor = useColorModeValue('gray.700', 'white');
  const listChannelGroup = useRecoilValue(listChannelGroupAtom);
  const totalRows = useRecoilValue(totalRow);
  const channelGroupActions = useChannelGroupActions();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCreate, setIsCreate] = useState(true);
  const [currentID, setCurrentID] = useState();
  const [isFetching, setIsFetching] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [refreshPagingDefault, setRefreshPagingDefault] = useState(false);

  const validationSchema = Yup.object().shape({
    group_name: Yup.string().required('This field is required'),
    level1: Yup.string().required('This field is required'),
    level2: Yup.string().required('This field is required'),
  });
  const formOptions = { resolver: yupResolver(validationSchema) };
  const { register, handleSubmit, formState, reset, setValue } = useForm(formOptions);
  const {
    register: registerSearch,
    handleSubmit: handleSubmitSearch,
    watch: watchSearch,
    reset: resetSearch,
  } = useForm({ defaultValues: { statusSearch: '1' } });
  const { errors, isSubmitting } = formState;
  const toast = useToast();

  const groupNameSearch = watchSearch('groupNameSearch');
  const level1Search = watchSearch('level1Search');
  const level2Search = watchSearch('level2Search');
  const statusSearch = watchSearch('statusSearch');

  useEffect(() => {
    if (listChannelGroup) {
      const mapped = listChannelGroup.map((i) => ({
        ...i,
        typeSwitch: 'channelGroup',
        filters: {
          group_name: groupNameSearch,
          level1: level1Search,
          level2: level2Search,
          status: statusSearch ? statusSearch : '1',
        },
      }));
      setTableData(mapped);
    }
  }, [listChannelGroup]);

  async function onSubmitSearch({ groupNameSearch, level1Search, level2Search, statusSearch }) {
    setIsFetching(true);
    await channelGroupActions.getListChannelGroup(
      {
        group_name: groupNameSearch,
        level1: level1Search,
        level2: level2Search,
        status: statusSearch,
      },
      0,
      localStorage && localStorage.getItem('currentPageSizeStore')
        ? localStorage.getItem('currentPageSizeStore')
        : 10
    );
    setRefreshPagingDefault(true);
    setIsFetching(false);
  }

  const recallApi = async (fetchDataOptions) => {
    setIsFetching(true);
    await channelGroupActions.getListChannelGroup(
      {
        group_name: groupNameSearch,
        level1: level1Search,
        level2: level2Search,
        status: statusSearch ? statusSearch : '1',
        sort_type: fetchDataOptions.sort_type,
        sort_field: fetchDataOptions.sort_field,
      },
      fetchDataOptions.pageIndex,
      fetchDataOptions.pageSize
    );
    setIsFetching(false);
  };

  async function onSubmit({ group_name, level1, level2, isActive }) {
    const params = {
      group_name,
      level1,
      level2,
      status: isActive ? 1 : 2,
    };
    try {
      let res;
      if (isCreate) {
        res = await channelGroupActions.createChannelGroup(params);
      } else {
        res = await channelGroupActions.updateChannelGroup(params, currentID);
      }
      if (res.status === 200) {
        onClose();
        reset();
        const message = isCreate
          ? 'Create Channel Group success'
          : 'Update Channel Group success';
        toast({
          description: message,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        await channelGroupActions.getListChannelGroup(
          {
            group_name: groupNameSearch,
            level1: level1Search,
            level2: level2Search,
            status: statusSearch ? statusSearch : '1',
          },
          0,
          localStorage && localStorage.getItem('currentPageSizeStore')
            ? localStorage.getItem('currentPageSizeStore')
            : 10
        );
      }
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function editChannelGroup(id) {
    const detail = await channelGroupActions.getChannelGroupDetail(id);
    if (detail) {
      const { group_name, level1, level2, status } = detail.data.data;
      setValue('group_name', group_name);
      setValue('level1', level1);
      setValue('level2', level2);
      setValue('isActive', status === 1 ? true : false);
      setCurrentID(id);
    }
    setIsCreate(false);
    onOpen();
  }

  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' display='block'>
          <Text
            float='left'
            color={textColor}
            fontSize='lg'
            fontWeight='bold'
            mb='6px'
          >
            Channel Group Management
          </Text>
          <Stack float='right' direction='row' spacing={4}>
            {checkForbidden(14) && (
              <Button
                size='sm'
                onClick={() => {
                  onOpen();
                  setIsCreate(true);
                  reset();
                }}
                leftIcon={<AddIcon />}
                colorScheme='blue'
                variant='outline'
              >
                Add Channel Group
              </Button>
            )}
          </Stack>
          <Modal
            closeOnOverlayClick={false}
            isOpen={isOpen}
            onClose={() => {
              onClose();
              reset();
            }}
            size='2xl'
          >
            <ModalOverlay />
            <form onSubmit={handleSubmit(onSubmit)}>
              <ModalContent>
                <ModalHeader>
                  {isCreate ? 'Add New Channel Group' : 'Edit Channel Group'}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                  <FormControl isRequired mb={4}>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Group Name
                    </FormLabel>
                    <Input
                      fontSize='sm'
                      ms='4px'
                      type='text'
                      placeholder='Group Name'
                      maxLength={250}
                      size='md'
                      {...register('group_name')}
                      isInvalid={errors.group_name}
                      errorBorderColor='red.300'
                    />
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.group_name?.message}
                    </Text>
                  </FormControl>
                  <FormControl isRequired mb={4}>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Level 1
                    </FormLabel>
                    <Select
                      ms='4px'
                      size='md'
                      {...register('level1')}
                      isInvalid={errors.level1}
                    >
                      <option value=''>-- Select Level 1 --</option>
                      {LEVEL1_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Select>
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.level1?.message}
                    </Text>
                  </FormControl>
                  <FormControl isRequired mb={4}>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Level 2
                    </FormLabel>
                    <Select
                      ms='4px'
                      size='md'
                      {...register('level2')}
                      isInvalid={errors.level2}
                    >
                      <option value=''>-- Select Level 2 --</option>
                      {LEVEL2_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Select>
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.level2?.message}
                    </Text>
                  </FormControl>
                  <FormControl>
                    <Switch
                      id='isChecked'
                      defaultChecked={isCreate && true}
                      {...register('isActive')}
                      size='sm'
                      ms='4px'
                    >
                      Active
                    </Switch>
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <Button
                    colorScheme='blue'
                    mr={3}
                    type='submit'
                    isLoading={isSubmitting}
                  >
                    {isCreate ? 'Create' : 'Update'}
                  </Button>
                  <Button
                    onClick={() => {
                      onClose();
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                </ModalFooter>
              </ModalContent>
            </form>
          </Modal>
        </Flex>
      </CardHeader>
      <CardBody>
        <Card>
          <form onSubmit={handleSubmitSearch(onSubmitSearch)}>
            <Flex
              direction='row'
              alignItems='center'
              mb={3}
              gap={4}
              flexWrap='wrap'
            >
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='xs' fontWeight='semibold'>
                  Group Name
                </FormLabel>
                <Input
                  _placeholder={{ color: 'gray.400' }}
                  variant='main'
                  type='text'
                  placeholder='Group Name'
                  minW='80px'
                  maxW='185px'
                  size='sm'
                  fontSize='sm'
                  {...registerSearch('groupNameSearch')}
                  _focus={{ borderColor: 'blue.500' }}
                />
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Level 1
                </FormLabel>
                <Select
                  ms='4px'
                  size='sm'
                  width='140px'
                  {...registerSearch('level1Search')}
                >
                  <option value=''>All</option>
                  {LEVEL1_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Level 2
                </FormLabel>
                <Select
                  ms='4px'
                  size='sm'
                  width='140px'
                  {...registerSearch('level2Search')}
                >
                  <option value=''>All</option>
                  {LEVEL2_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Status
                </FormLabel>
                <Select
                  ms='4px'
                  size='sm'
                  width='100px'
                  {...registerSearch('statusSearch')}
                >
                  {STATUS.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
            </Flex>
            <Flex justify='center' align='center' gap='8px' mt={3}>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<RxReset />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  onClick={() => resetSearch()}
                >
                  Reset
                </Button>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<BsSearch />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  type='submit'
                >
                  Search
                </Button>
              </Box>
            </Flex>
          </form>
        </Card>
        <SearchTable2
          tableData={tableData}
          columnsData={columnsDataChannelGroup}
          totalRow={totalRows.channelGroups}
          edit={editChannelGroup}
          listPermissions={[13]}
          actionsActive={['edit']}
          loadData={(fetchDataOptions) => recallApi(fetchDataOptions)}
          isFetching={isFetching}
          refreshPagingDefault={refreshPagingDefault}
        />
      </CardBody>
    </Card>
  );
}

export default DataTableChannelGroups;
